import { Wifi, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

interface WifiQualityScaleProps {
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}

const wifiLevels = [
  { value: 1, label: "Unusable", bars: 0 },
  { value: 2, label: "Poor", bars: 1 },
  { value: 3, label: "Okay", bars: 2 },
  { value: 4, label: "Good", bars: 3 },
  { value: 5, label: "Excellent", bars: 4 },
];

function WifiIcon({ bars, isSelected }: { bars: number; isSelected: boolean }) {
  if (bars === 0) {
    return <WifiOff className={cn("h-5 w-5", isSelected ? "text-destructive" : "text-muted-foreground")} />;
  }
  
  // Create a visual representation of signal strength
  return (
    <div className="flex items-end gap-0.5 h-5">
      {[1, 2, 3, 4].map((bar) => (
        <div
          key={bar}
          className={cn(
            "w-1 rounded-sm transition-colors",
            bar <= bars ? (isSelected ? "bg-primary" : "bg-muted-foreground") : "bg-muted",
          )}
          style={{ height: `${bar * 4 + 4}px` }}
        />
      ))}
    </div>
  );
}

export function WifiQualityScale({ value, onChange, disabled = false }: WifiQualityScaleProps) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-1">
        {wifiLevels.map((level) => (
          <button
            key={level.value}
            type="button"
            onClick={() => onChange(level.value)}
            disabled={disabled}
            className={cn(
              "flex-1 flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all duration-200",
              "border-2",
              value === level.value
                ? "border-primary bg-primary/10 scale-105"
                : "border-transparent bg-secondary/30 hover:bg-secondary/50",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          >
            <WifiIcon bars={level.bars} isSelected={value === level.value} />
            <span className="text-[10px] font-medium">{level.value}</span>
          </button>
        ))}
      </div>
      
      {/* Label showing current selection */}
      <div className="text-center">
        {value ? (
          <p className="text-sm font-medium">{wifiLevels[value - 1].label}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Tap to rate WiFi quality</p>
        )}
      </div>
    </div>
  );
}
